/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 관심표명자 목록 컴포넌트
 * 버전 : 1.0
 * 파일명 : InterestedList.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-18, 강동형 : 저작권 추가, 디자인 및 레이아웃 변경
 * 수정 : 2026-02-02, 강동형 : 학과 추가
 * 수정 : 2026-02-03, 강동형 : 메모 최소 길이 가지도록 변경
 * 수정 : 2026-02-05, 강동형 : 학과 데이터가 비어있어도 보여주도록 수정
 *================================================================= */

import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { interested } from 'src/types/InterestedType';
import NotepadIcon from 'src/assets/images/Icon_NotePad.svg'
import {styled} from 'styled-components'

interface StatusBoxProps {
  status : string;
  isLast? : boolean
}

const StatusBox = styled.div<StatusBoxProps>`
  background-color: #fff;
  color: ${({ status }) => ({
    '관심': '#FFB313',
    '지원준비': '#1976FF',
    '지원완료': '#288E43',
    '임관': '#FF7D4D',
    '관심없음': '#FF5A5A'
  }[status] || '#000')};
  padding: 4px;
  font-size: 0.75rem;
  width: 60px;
  text-align: center;
  border-radius: 4px;
`;

const ListWrapper = styled.div<StatusBoxProps>`
  display: flex;
  align-items: center;
  padding: 12px 10px;
  margin: 16px 20px 0px 20px;
  background-color: ${({ status }) => ({
    '관심': '#FCCA61',
    '지원준비': '#68A5FF',
    '지원완료': '#69B087',
    '임관': '#FF936C',
    '관심없음': '#FF8584'
  }[status] || 'white')};
  border-radius: 8px;
  box-shadow: 0px 0px 7px 0px rgba(0, 0, 0, 0.25);
  margin-bottom: ${({ isLast }) => (isLast ? '8px' : '0px')};
`

const ListContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;

  width: 100%;
`;

const ExpandWrapper = styled.div`
  background-color: #FFFFFF;
  margin: 0px -10px;
  margin-bottom: -12px;
  padding : 10px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`

const ExpandContainer = styled.div`
  background-color: #F8F8F8;
  padding: 12px 20px;
  border-radius: 8px;
`

interface Props{
    listed: interested
    setSelected: React.SetStateAction<any>
    isLast : boolean
}

const InterestedList = ({listed, setSelected, isLast}: Props) => {
    const list = listed
    
    const [expandedStudent, setExpandedStudent] = useState<number | null>(null);
    const handleStudentClick = (studentId: number) => {
        setExpandedStudent((prev) => (prev === studentId ? null : studentId));
      };

    const onClickModify = (e:any)=>{
      e.stopPropagation(); // ✅ 이벤트 버블링 막기
      setSelected(listed)
    }

  return (
      <ListWrapper status={list!.status} isLast={isLast}>
        <div style={{display:'flex', flexDirection:'column', gap:'16px', width: '100%'}}>
          <ListContainer onClick={() => handleStudentClick(list!.id)}>
                <StatusBox status={list!.status}>{list?.status}</StatusBox>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '16px',whiteSpace:'nowrap', color: '#FFFFFF'}}>
                    {list?.name}
                  </Typography>
                  <Typography variant="body2" sx={{color: '#FFFFFF', fontSize: '14px', flex:1}}>
                      {list.school?.school_name}
                      {list.university?.university_name}
                  </Typography>
          <IconButton onClick={(e) => {onClickModify(e)}} style={{width:'30px', height:'30px', marginLeft:'auto',zIndex:1}}>
              <img src={NotepadIcon} style={{fill:'white'}}/>
          </IconButton>
          </ListContainer>
          {expandedStudent === list?.id && (
            <ExpandWrapper>
              <ExpandContainer>
                <Box sx={{ display: 'flex', alignItems: 'center'}}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5 }}>생년월일</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.birthday.toString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center'}}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5}}>성별</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.gender}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5}}>학교</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.school?.school_name}{list.university?.university_name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5}}>학과</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.major}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5}}>상담일</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.counsel_date.toString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5}}>휴대폰</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.phone}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'black', width: '80px', fontSize: '0.8rem', lineHeight: '1.5rem', opacity:0.5, minWidth:'80px'}}>메모</Typography>
                  <Typography sx={{ color: 'black', fontSize: '0.8rem' }}>{list.memo}</Typography>
                </Box>
              </ExpandContainer>
            </ExpandWrapper>
            )}
          </div>
      </ListWrapper>
  )
}

export default InterestedList